import React, { useState, useEffect } from "react";
import { LayoutDashboard, Car, Users as UsersIcon, Wrench, DollarSign,
  Shield, CalendarDays, Receipt, CalendarRange, Bell, HelpCircle, BarChart3, Wallet, Sparkles, IdCard, HeartHandshake, Radar, Trash2, MessageSquare, ClipboardCheck, FileSignature, Radio } from "lucide-react";
import { api } from "../../lib/api";

import DashboardTab from "./DashboardTab";
import AnalyticsTab from "./AnalyticsTab";
import DepositsTab from "./DepositsTab";
import BookingsTab from "./BookingsTab";
import ScheduleTab from "./ScheduleTab";
import InvoicesTab from "./InvoicesTab";
import DriverRentalsTab from "./DriverRentalsTab";
import FleetTab from "./FleetTab";
import EventsTab from "./EventsTab";
import DriversTab from "./DriversTab";
import TeamTab from "./TeamTab";
import UsersTab from "./UsersTab";
import FAQTab from "./FAQTab";
import SpecialsTab from "./SpecialsTab";
import RentalsTab from "./RentalsTab";
import AddOnsTab from "./AddOnsTab";
import CustomersTab from "./CustomersTab";
import CRM from "./CRM";
import CommandCenterTab from "./CommandCenterTab";
import OpsBoardTab from "./OpsBoardTab";
import FleetCommandTab from "./FleetCommandTab";
import TrashTab from "./TrashTab";
import InspectionsTab from "./InspectionsTab";
import ContractsTab from "./ContractsTab";
import GlobalSearch from "../../components/GlobalSearch";

const TABS = [
  { key: "ops",            label: "Ops Board",      icon: Radar,           Component: OpsBoardTab },
  { key: "fleet_command",  label: "Fleet Command",  icon: Radio,           Component: FleetCommandTab },
  { key: "command",        label: "Command Center", icon: Radar,           Component: CommandCenterTab },
  { key: "dashboard",      label: "Dashboard",      icon: LayoutDashboard, Component: DashboardTab },
  { key: "crm",            label: "CRM",            icon: HeartHandshake,  Component: CRM },
  { key: "analytics",      label: "Analytics",      icon: BarChart3,        Component: AnalyticsTab },
  { key: "deposits",       label: "Deposits",       icon: Wallet,           Component: DepositsTab },
  { key: "schedule",       label: "Schedule",       icon: CalendarRange,    Component: ScheduleTab },
  { key: "bookings",       label: "Bookings",       icon: DollarSign,       Component: BookingsTab },
  { key: "rentals",        label: "Rentals",        icon: IdCard,           Component: RentalsTab },
  { key: "inspections",    label: "Inspections",    icon: ClipboardCheck,   Component: InspectionsTab },
  { key: "contracts",      label: "Contracts",      icon: FileSignature,    Component: ContractsTab },
  { key: "specials",       label: "Specials",       icon: Sparkles,         Component: SpecialsTab },
  { key: "addons",         label: "Add-ons",        icon: Sparkles,         Component: AddOnsTab },
  { key: "invoices",       label: "Invoices",       icon: Receipt,          Component: InvoicesTab },
  { key: "driver_rentals", label: "Driver Rentals", icon: Car,              Component: DriverRentalsTab },
  { key: "fleet",          label: "Fleet",          icon: Car,              Component: FleetTab },
  { key: "events",         label: "Events",         icon: CalendarDays,     Component: EventsTab },
  { key: "faq",            label: "FAQ",            icon: HelpCircle,       Component: FAQTab },
  { key: "drivers",        label: "Drivers",        icon: UsersIcon,        Component: DriversTab },
  { key: "customers",      label: "Customers",      icon: UsersIcon,        Component: CustomersTab },
  { key: "team",           label: "Team",           icon: Shield,           Component: TeamTab },
  { key: "users",          label: "Users",          icon: Wrench,           Component: UsersTab },
  { key: "trash",          label: "Trash",          icon: Trash2,           Component: TrashTab },
];

export default function BackOffice() {
  const [tab, setTab] = useState(() => {
    const hash = (window.location.hash || "").replace("#", "").trim();
    return TABS.find((t) => t.key === hash) ? hash : "command";
  });
  const [approvalStats, setApprovalStats] = useState({ bookings: 0, inquiries: 0, total: 0 });
  const Active = TABS.find((t) => t.key === tab)?.Component;

  // Listen for hash changes — Quick Actions in the Command Center set
  // `window.location.hash` to jump to another tab (e.g. #schedule, #crm).
  useEffect(() => {
    const onHash = () => {
      const h = (window.location.hash || "").replace("#", "").trim();
      if (TABS.find((t) => t.key === h)) setTab(h);
    };
    window.addEventListener("hashchange", onHash);
    // Deep-link from a booking row → jump to the Invoices tab and highlight
    // the source invoice row.
    const onJumpToInvoice = (e) => {
      setTab("invoices");
      const invId = e?.detail?.invoice_id;
      // Scroll & flash the row after the tab has mounted.
      setTimeout(() => {
        if (!invId) return;
        const el = document.querySelector(`[data-testid="bo-invoice-${invId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-amber-400", "ring-offset-2", "ring-offset-black");
          setTimeout(() => el.classList.remove("ring-2", "ring-amber-400", "ring-offset-2", "ring-offset-black"), 2200);
        }
      }, 250);
    };
    window.addEventListener("bo:jump-to-invoice", onJumpToInvoice);
    return () => {
      window.removeEventListener("hashchange", onHash);
      window.removeEventListener("bo:jump-to-invoice", onJumpToInvoice);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchCount = () =>
      api.get("/admin/pending-approval-count")
        .then((r) => { if (!cancelled) setApprovalStats(r.data || { bookings: 0, inquiries: 0, total: 0 }); })
        .catch(() => { /* silent */ });
    fetchCount();
    const iv = setInterval(fetchCount, 30000); // refresh every 30s
    return () => { cancelled = true; clearInterval(iv); };
  }, [tab]);

  return (
    <section data-testid="backoffice-page" className="mx-auto max-w-7xl px-5 sm:px-8 py-12">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div>
            <div className="label-caps text-burgundy mb-2">Back Office</div>
            <h1 className="font-serif text-4xl sm:text-5xl">Plenty Rydez Dispatch</h1>
          </div>
          <GlobalSearch />
          {approvalStats.total > 0 && (
            <button
              type="button"
              data-testid="bo-approval-badge"
              onClick={() => setTab(approvalStats.bookings ? "bookings" : "rentals")}
              className="ml-2 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-400/50 bg-amber-400/15 text-amber-200 text-sm hover:bg-amber-400/25 transition"
              title="Needs approval"
            >
              <Bell size={14} className="animate-pulse" />
              <span className="label-caps">
                {approvalStats.bookings || approvalStats.total} booking{(approvalStats.bookings || approvalStats.total) === 1 ? "" : "s"} awaiting approval
              </span>
            </button>
          )}
        </div>
        <div className="glass rounded-full p-1 flex flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`bo-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-full text-xs label-caps transition-colors flex items-center gap-1.5 ${
                tab === t.key ? "bg-burgundy text-white" : "text-white/60 hover:text-white"
              }`}
            >
              <t.icon size={12} /> {t.label}
              {t.key === "bookings" && approvalStats.bookings > 0 && (
                <span className="ml-1 px-1.5 py-0 rounded-full text-[9px] bg-amber-400 text-black">{approvalStats.bookings}</span>
              )}
            </button>
          ))}
        </div>
      </div>
      {Active ? <Active /> : null}
    </section>
  );
}
